// Balance formulas shared by the game and tests/balance.test.ts. Keep three.js out of here.
import type { PlayerMods, ShopDef, SpellDef, SpellKind, SpellStats, StageDef, StatDelta } from './content.ts';
import { ENEMIES, SHOP } from './content.ts';

export const MAX_SPELLS = 6;
export const MAX_PASSIVES = 6;
export const SPELL_MAX_LEVEL = 6;
export const BASE_HP = 100;
export const BASE_SPEED = 5.2;
export const BASE_MAGNET = 2.8;
export const BASE_CRIT = 0.05;
export const MAX_STORM_TIER = 5;

export interface StormMultipliers {
  hp: number;
  dmg: number;
  spawnRate: number;
  goldBonus: number;
}

export function stormMultipliers(tier: number): StormMultipliers {
  const t = Math.max(0, Math.min(MAX_STORM_TIER, Math.floor(tier)));
  return {
    hp: 1 + t * 0.28,
    dmg: 1 + t * 0.18,
    spawnRate: 1 + t * 0.1,
    goldBonus: t * 0.25,
  };
}

const DEFAULT_STATS: SpellStats = {
  damage: 0,
  cooldown: 1,
  count: 1,
  area: 1,
  speed: 10,
  duration: 0,
  pierce: 1,
  chains: 0,
  knockback: 0,
  tick: 0.5,
  slow: 0,
  range: 10,
};

export function emptyMods(): PlayerMods {
  return {
    might: 0, haste: 0, area: 0, amount: 0, moveSpeed: 0, maxHp: 0, magnet: 0, growth: 0,
    armor: 0, regen: 0, duration: 0, crit: 0, greed: 0, luck: 0, revive: 0, reroll: 0,
  };
}

export function addMods(target: PlayerMods, delta: Partial<PlayerMods>, times = 1): PlayerMods {
  for (const key of Object.keys(delta) as Array<keyof PlayerMods>) {
    target[key] += (delta[key] ?? 0) * times;
  }
  return target;
}

export function cooldownMult(mods: PlayerMods): number {
  return Math.max(0.4, 1 - mods.haste);
}

function addDelta(stats: SpellStats, delta: StatDelta): void {
  for (const key of Object.keys(delta) as Array<keyof SpellStats>) {
    stats[key] += delta[key] ?? 0;
  }
}

export function spellStats(def: SpellDef, level: number, evolved: boolean): SpellStats {
  const stats = { ...DEFAULT_STATS, ...def.base };
  for (let i = 0; i < level - 1 && i < def.upgrades.length; i += 1) addDelta(stats, def.upgrades[i]);
  return evolved ? { ...stats, ...def.evolution.stats } : stats;
}

export function effectiveStats(def: SpellDef, raw: SpellStats, mods: PlayerMods): SpellStats {
  return {
    ...raw,
    damage: raw.damage * (1 + mods.might),
    cooldown: Math.max(0.05, raw.cooldown * cooldownMult(mods)),
    count: def.kind === 'aura' ? raw.count : raw.count + mods.amount,
    area: raw.area * (1 + mods.area),
    duration: raw.duration * (1 + mods.duration),
  };
}

// Experience needed to go from `level` to `level + 1`.
export function xpToNext(level: number): number {
  return Math.round(6 + (level - 1) * 5.5 + Math.max(0, level - 20) * 4 + Math.max(0, level - 30) * 8);
}

export function stageRamp(t: number): number {
  const x = Math.min(1, Math.max(0, t / 300));
  return x * x * (3 - 2 * x);
}

export function spawnRate(t: number, stage: StageDef, stormTier = 0): number {
  return (1.0 + (0.95 * t) / 60) * stage.rateMult * stormMultipliers(stormTier).spawnRate;
}

export function spawnInterval(t: number, stage: StageDef, stormTier = 0): number {
  return 1 / spawnRate(t, stage, stormTier);
}

export function maxAlive(t: number): number {
  return Math.min(300, 45 + t * 0.55);
}

// Quadratic in minutes: player power compounds (levels x multipliers), so the horde must too.
export function hpScale(t: number, stage: StageDef, stormTier = 0): number {
  const m = t / 60;
  const overtime = t > 480 ? Math.pow(1.22, (t - 480) / 60) : 1;
  return (1 + 0.25 * m + 0.06 * m * m) * (1 + (stage.hpMult - 1) * stageRamp(t)) * stormMultipliers(stormTier).hp * overtime;
}

export function dmgScale(t: number, stage: StageDef, stormTier = 0): number {
  const overtime = t > 480 ? 1 + ((t - 480) / 60) * 0.18 : 1;
  return (1 + (0.08 * t) / 60) * (1 + (stage.dmgMult - 1) * stageRamp(t)) * stormMultipliers(stormTier).dmg * overtime;
}

export function rosterWeights(t: number, stage: StageDef): Array<[string, number]> {
  const out: Array<[string, number]> = [];
  for (const [id, from, weight, to] of stage.roster) {
    if (t >= from && (to === undefined || t < to)) out.push([id, weight]);
  }
  return out;
}

export function averageEnemy(t: number, stage: StageDef): { hp: number; xp: number } {
  let total = 0;
  let hp = 0;
  let xp = 0;
  for (const [id, weight] of rosterWeights(t, stage)) {
    const def = ENEMIES[id];
    total += weight;
    hp += def.hp * weight;
    xp += def.xp * weight;
  }
  return { hp: (hp / total) * hpScale(t, stage), xp: ((xp / total) * stage.xpMult) };
}

export const ELITE_HP_MULT = 14;
export const ELITE_TIMES = [120, 240, 360, 420];

export function bossHp(stage: StageDef): number {
  return ENEMIES[stage.boss].hp;
}

// --- Damage estimates (used by the balance simulation; the game itself simulates hits) ---

// Enemies per square unit near the player; the horde converges on you, so this grows with alive count.
export function nearDensity(alive: number): number {
  return alive / 320;
}

function areaHits(radius: number, alive: number, floor = 1): number {
  if (alive <= 0) return 0;
  return Math.min(alive, Math.max(floor, nearDensity(alive) * Math.PI * radius * radius));
}

export function estimateDps(kind: SpellKind, s: SpellStats, alive: number, critChance = BASE_CRIT): { single: number; crowd: number } {
  const crit = 1 + critChance;
  const cd = Math.max(0.05, s.cooldown);
  let single = 0;
  let crowd = 0;
  switch (kind) {
    case 'missile':
    case 'volley': {
      single = (s.damage * s.count) / cd;
      crowd = single * Math.min(s.pierce, Math.max(1, alive / 60));
      break;
    }
    case 'bomb': {
      single = (s.damage * s.count) / cd;
      const burn = s.duration > 0 ? (s.damage * 0.2 * s.duration) / s.tick : 0;
      crowd = ((s.damage + burn) * s.count * areaHits(s.area, alive)) / cd;
      break;
    }
    case 'nova':
      single = (s.damage * s.count) / cd;
      crowd = single * areaHits(s.area, alive);
      break;
    case 'chain':
      single = (s.damage * s.count) / cd;
      crowd = single * Math.min(1 + s.chains, Math.max(1, alive));
      break;
    case 'strike':
      single = (s.damage * s.count) / cd;
      crowd = single * areaHits(s.area, alive);
      break;
    case 'beam': {
      single = (s.damage * s.count) / cd;
      const inLine = Math.min(alive, 1 + nearDensity(alive) * s.range * s.area * 0.6);
      crowd = single * Math.max(1, inLine);
      break;
    }
    case 'boomerang': {
      single = (s.damage * s.count * 2) / Math.max(cd, s.tick * 2);
      const outDist = s.speed * 0.55;
      const path = Math.min(alive, 1 + nearDensity(alive) * outDist * 1.2 * s.area);
      crowd = ((s.damage * s.count * 2) / cd) * Math.max(1, path);
      break;
    }
    case 'orbit': {
      const uptime = s.duration > 1000 ? 1 : s.duration / (s.duration + cd);
      const revTime = (Math.PI * 2) / s.speed;
      const band = Math.min(alive, nearDensity(alive) * Math.PI * 2 * s.area * 1.4);
      const hitsPerSec = Math.min((s.count * band) / revTime, band / s.tick);
      crowd = s.damage * hitsPerSec * uptime;
      single = s.damage * Math.min(s.count / revTime, 1 / s.tick) * uptime * 0.5;
      break;
    }
    case 'zone': {
      const pools = (s.count * s.duration) / cd;
      single = (s.damage / s.tick) * Math.min(pools, 1) * 0.7;
      crowd = (s.damage / s.tick) * pools * areaHits(s.area, alive);
      break;
    }
    case 'vortex': {
      const wells = (s.count * s.duration) / cd;
      single = (s.damage / s.tick) * Math.min(wells, 1);
      crowd = (s.damage / s.tick) * wells * areaHits(s.area, alive) * 1.5;
      break;
    }
    case 'aura':
      single = s.damage / s.tick;
      crowd = single * areaHits(s.area, alive, alive > 0 ? 1 : 0);
      break;
    case 'burst':
      // A ring of shards: only a few reach one target, and some fly off into empty space.
      single = (s.damage * (1 + s.count / 8)) / cd;
      crowd = ((s.damage * s.count) / cd) * Math.min(s.pierce, Math.max(1, alive / 60)) * 0.7;
      break;
    case 'drain':
      single = s.damage / cd;
      crowd = (s.damage * Math.min(s.count, alive)) / cd;
      break;
    case 'rune':
      // A rune only fires when something walks onto it, so a lone target trips fewer of them.
      single = ((s.damage * s.count) / cd) * 0.6;
      crowd = (s.damage * s.count * areaHits(s.area, alive)) / cd;
      break;
    case 'wave': {
      single = ((s.damage * s.count) / cd) * 0.7;
      const swept = Math.min(alive, nearDensity(alive) * s.range * s.area * 2 * 0.6);
      crowd = ((s.damage * s.count) / cd) * Math.max(1, swept);
      break;
    }
  }
  return { single: single * crit, crowd: crowd * crit };
}

// --- UI helpers ---

const STAT_LABELS: Record<keyof SpellStats, string> = {
  damage: 'Damage',
  cooldown: 'Cooldown',
  count: 'Amount',
  area: 'Area',
  speed: 'Speed',
  duration: 'Duration',
  pierce: 'Pierce',
  chains: 'Chains',
  knockback: 'Force',
  tick: 'Tick',
  slow: 'Slow',
  range: 'Range',
};

export function upgradeText(delta: StatDelta): string {
  const parts: string[] = [];
  for (const key of Object.keys(delta) as Array<keyof SpellStats>) {
    const value = delta[key] ?? 0;
    if (key === 'slow') parts.push(`+${Math.round(value * 100)}% Slow`);
    else if (key === 'tick') parts.push('Faster ticks');
    else if (key === 'cooldown') parts.push(`${value}s Cooldown`);
    else if (key === 'duration') parts.push(`+${value}s Duration`);
    else parts.push(`${value > 0 ? '+' : ''}${value} ${STAT_LABELS[key]}`);
  }
  return parts.join(', ');
}

export function shopCost(item: ShopDef, rank: number): number {
  return Math.round((item.baseCost * Math.pow(rank + 1, 1.5)) / 10) * 10;
}

// One hit on the player: armour first, then half a second of i-frames, except for a hazard's damage-over-time tick,
// which would otherwise make the player immune to the horde while it lasts. Existing i-frames block every hit.
export function playerHit(invuln: number, amount: number, armor: number, iframes: boolean): { dmg: number; invuln: number } {
  if (invuln > 0) return { dmg: 0, invuln };
  return { dmg: Math.max(1, amount - armor), invuln: iframes ? 0.5 : 0 };
}

// The cheapest Grimoire rank the player can buy right now, for the "you can afford an upgrade" nudge.
export function affordableUpgrade(shop: Record<string, number>, gold: number): ShopDef | null {
  let best: ShopDef | null = null;
  let bestCost = Infinity;
  for (const item of SHOP) {
    const rank = shop[item.id] ?? 0;
    const cost = shopCost(item, rank);
    if (rank < item.maxRank && cost <= gold && cost < bestCost) [best, bestCost] = [item, cost];
  }
  return best;
}

// --- main (account) level: xp from every run, a gold reward per level, and a small capped stat bonus ---
export const MAIN_BONUS_LEVELS = 20; // the stat bonus stops growing after this many level-ups
const MAIN_MAX_LEVEL = 99;

// Grows with the square of the level: a first clear is one level, the capped bonus is a long-term goal.
export const mainXpToNext = (level: number): number => 600 + 300 * level + 50 * level * level;

export function mainLevel(xp: number): { level: number; into: number; need: number } {
  let level = 1;
  let into = Math.max(0, Math.floor(xp));
  while (level < MAIN_MAX_LEVEL && into >= mainXpToNext(level)) {
    into -= mainXpToNext(level);
    level += 1;
  }
  return { level, into, need: mainXpToNext(level) };
}

export function runXp(kills: number, seconds: number, stageIndex: number, victory: boolean): number {
  return Math.round((kills + seconds) * (1 + 0.25 * stageIndex) + (victory ? 300 * (stageIndex + 1) : 0));
}

// Gold paid on reaching `level`.
export const mainLevelGold = (level: number): number => 30 + 10 * level;

export function mainLevelMods(level: number): Pick<PlayerMods, 'might' | 'maxHp'> {
  const ups = Math.min(MAIN_BONUS_LEVELS, Math.max(0, level - 1));
  return { might: ups / 100, maxHp: 2 * ups };
}

export function runGold(kills: number, seconds: number, stageIndex: number, victory: boolean, greed: number, stormTier = 0): number {
  const base = kills / 8 + seconds / 6 + (victory ? 150 * (stageIndex + 1) : 0);
  return Math.round(base * (1 + greed + stormMultipliers(stormTier).goldBonus));
}

// --- Per-mage mastery (Ranks 0..5): xp accumulated while playing a specific mage ---
export const MAX_MAGE_MASTERY = 5;
export const MASTERY_THRESHOLDS: readonly number[] = [500, 1200, 2500, 4800, 8000];

export function mageMastery(xp: number): { rank: number; into: number; need: number } {
  let rank = 0;
  let into = Math.max(0, Math.floor(xp));
  while (rank < MAX_MAGE_MASTERY && into >= MASTERY_THRESHOLDS[rank]) {
    into -= MASTERY_THRESHOLDS[rank];
    rank += 1;
  }
  const need = rank < MAX_MAGE_MASTERY ? MASTERY_THRESHOLDS[rank] : MASTERY_THRESHOLDS[MAX_MAGE_MASTERY - 1];
  return { rank, into, need };
}

export function mageMasteryMods(rank: number): Pick<PlayerMods, 'might' | 'maxHp'> {
  const r = Math.max(0, Math.min(MAX_MAGE_MASTERY, Math.floor(rank)));
  return { might: r * 0.04, maxHp: r * 5 };
}
